<template>
  <template v-if="baseImages.length">
    <LazyHydrationGalleryMobile
      class="md:hidden"
      :hydrate-on-media-query="`(max-width: ${DESKTOP_BREAKPOINT - 1}px)`"
      :items="mobileItems"
      :product-name="props.trackingProductModel"
      :tracking-product-model="props.trackingProductModel"
      :with-sticker="shouldDisplaySticker"
      @image-click="onImageClick"
      @review-visible="onReviewVisible"
      @sticker-click="handleStickerClick"
    >
      <template #sticker="{ size }">
        <slot name="sticker" :size />
      </template>
    </LazyHydrationGalleryMobile>
    <LazyHydrationGalleryDesktop
      class="hidden md:block"
      :current-active-index
      :hydrate-on-media-query="`(min-width: ${DESKTOP_BREAKPOINT}px)`"
      :items="desktopItems"
      :product-name="props.trackingProductModel"
      :tracking-product-model="props.trackingProductModel"
      :with-sticker="shouldDisplaySticker"
      @image-click="onImageClick"
      @review-visible="onReviewVisible"
      @sticker-click="handleStickerClick"
      @update:current-active-index="setCurrentActiveIndex"
    >
      <template #sticker="{ size }">
        <slot name="sticker" :size />
      </template>
    </LazyHydrationGalleryDesktop>
    <GalleryMediaViewer
      v-if="allowMediaViewer"
      v-model:current-index="mediaViewerIndex"
      :images="baseImages"
    />
  </template>
</template>

<script setup lang="ts">
import { defineLazyHydrationComponent, useHead } from '#imports'
import { computed, toRef, watch } from 'vue'

import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'
import { openModal } from '@ds/components/ModalBase'
import { useIllustrationOptimizer } from '@ds/plugin'
import { css } from '@ds/utils/css'

import { MAIN_IMG_SIZES } from '~/scopes/product-core/components/presentationals/GalleryNew/GalleryNew.constants'
import type { ReviewWithCount } from '~/scopes/product-core/components/presentationals/GalleryNew/GalleryNew.types'
import type { ProductDetailPageGalleryImage } from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/ProductDetailPageGallery.types'
import { useGalleryTracking } from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/composables/useGalleryTracking/useGalleryTracking'
import { useFetchGalleryData } from '~/scopes/product-core/composables/useFetch/useFetchGalleryData/useFetchGalleryData'
import { MODAL_NAMES } from '~/scopes/product-core/constants'

import { DESKTOP_BREAKPOINT } from './ProductDetailPageGallery.constants'
import GalleryMediaViewer from './components/GalleryMediaViewer.vue'
import { useGalleryInteractions } from './composables/useGalleryInteractions/useGalleryInteractions'
import { useGalleryLayout } from './composables/useGalleryLayout/useGalleryLayout'

const LazyHydrationGalleryMobile = defineLazyHydrationComponent(
  'mediaQuery',
  () => import('./components/GalleryMobile/GalleryMobile.vue'),
)
const LazyHydrationGalleryDesktop = defineLazyHydrationComponent(
  'mediaQuery',
  () => import('./components/GalleryDesktop/GalleryDesktop.vue'),
)

const props = withDefaults(
  defineProps<{
    allowMediaViewer?: boolean
    allowPartnersImages?: boolean
    trackingProductModel?: string
    areReviewsPending?: boolean
    reviewsFromProduct?: ReviewWithCount[] | null
    withSticker?: boolean
    productInfo: {
      images: GetProductResponse['images']
      type: GetProductResponse['type']
      brand?: string
      categoryId?: string
    }
  }>(),
  {
    allowMediaViewer: true,
    allowPartnersImages: true,
    trackingProductModel: '',
    areReviewsPending: false,
    reviewsFromProduct: undefined,
    withSticker: true,
  },
)

const emit = defineEmits<{
  (e: 'sticker-click'): void
}>()

defineSlots<{
  sticker: { size: 'default' | 'small' }
}>()

const shouldDisplaySticker = computed(
  () => props.withSticker && props.productInfo.type === 'Refurbished',
)

const { data } = await useFetchGalleryData({
  productId: toRef(props, 'trackingProductModel'),
  images: toRef(props.productInfo, 'images'),
  allowPartnersImages: toRef(props, 'allowPartnersImages'),
  reviewsFromProduct: computed(() => props.reviewsFromProduct ?? undefined),
  areReviewsPending: toRef(props, 'areReviewsPending'),
  brand: toRef(props?.productInfo, 'brand'),
  categoryId: toRef(props?.productInfo, 'categoryId'),
})

const baseImages = computed(() => data.value?.baseImages ?? [])
const injectableItems = computed(() => data.value?.injectableItems ?? [])

const { mobileItems, desktopItems } = useGalleryLayout({
  baseImages,
  injectableItems,
})

const optimizedImage = useIllustrationOptimizer(
  () => baseImages.value?.[0]?.src,
)

useHead({
  style: [
    {
      key: 'product-detail-page-gallery-critical-styles',
      id: 'product-detail-page-gallery-critical-styles',
      tagPriority: 'critical',
      textContent: css`
        :root {
          --carousel-max-size: 45lvh;

          --carousel-big-item-size: calc(80vw - 26px);
          --carousel-big-item-width: var(--carousel-big-item-size);
          --carousel-big-item-max-size: var(--carousel-max-size);

          --gap-between-images: 12px;
          /* Small items are only relevant on mobile */
          --carousel-small-item-size: calc(
            (var(--carousel-big-item-size) / 2) - var(--gap-between-images)
          );
          --carousel-small-item-max-size: calc(
            (var(--carousel-max-size) / 2) - var(--gap-between-images)
          );
        }

        @media (min-width: 768px) {
          :root {
            /* used both in the main image and the main image */
            --carousel-big-item-width: calc(80vw - 26px);
            /* fit in content or span to '--carousel-big-item-width' if possible */
            --carousel-big-item-size: 100%;
            --carousel-max-size: 448px;

            --carousel-small-item-size: 104px;
          }
        }

        @media (min-width: 1200px) {
          :root {
            --carousel-big-item-size: 448px;
          }
        }
      `,
    },
  ],
  link: [
    {
      rel: 'preload',
      href: optimizedImage.value.src,
      as: 'image',
      imagesrcset: optimizedImage.value.srcset,
      imagesizes: MAIN_IMG_SIZES,
    },
  ],
})

const maxIndex = computed(() => Math.max(desktopItems.value.length - 1, 0))
const {
  currentActiveIndex,
  mediaViewerIndex,
  resetCurrentActiveIndex,
  setCurrentActiveIndex,
} = useGalleryInteractions(maxIndex)
const { onSlideImpression } = useGalleryTracking(props.trackingProductModel)

const breakpoint = useClientOnlyComputedBreakpoint()
const isMobile = computed(
  () =>
    breakpoint.value === Breakpoint.XS || breakpoint.value === Breakpoint.SM,
)

watch(isMobile, (isMobileNow, wasMobile) => {
  if (isMobileNow && !wasMobile) {
    resetCurrentActiveIndex()
  }
})

watch(maxIndex, () => {
  setCurrentActiveIndex(currentActiveIndex.value)
})

const seenReviewIds = new Set<string>()

function onImageClick(image: ProductDetailPageGalleryImage) {
  if (!props.allowMediaViewer) {
    return
  }

  const baseImageIndex = baseImages.value.findIndex(
    (item) => item.id === image.id,
  )
  if (baseImageIndex < 0) {
    return
  }

  mediaViewerIndex.value = baseImageIndex
  openModal(MODAL_NAMES.MEDIA_VIEWER)
}

function onReviewVisible(
  review: ReviewWithCount,
  index: number,
  totalSlides: number,
) {
  if (seenReviewIds.has(review.id)) {
    return
  }

  seenReviewIds.add(review.id)

  onSlideImpression({
    currentSlide: index,
    totalSlides,
    itemKind: 'review',
  })
}

function handleStickerClick() {
  emit('sticker-click')
}
</script>
